import { useEffect, useCallback, useRef, useState } from 'react';
import { useDebounce } from './use-debounce';

export interface AutoSaveData {
  content: any;
  timestamp: string;
  id: string;
  type: 'chapter' | 'note';
}

export interface UseAutoSaveOptions {
  /** The content to auto-save */
  content: any;
  /** Unique identifier for the content (chapterId or noteId) */
  id: string;
  /** Type of content being saved */
  type: 'chapter' | 'note';
  /** Delay in milliseconds before saving (default: 500ms) */
  delay?: number;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
  /** Callback when content is auto-saved */
  onAutoSave?: (data: AutoSaveData) => void;
}

export interface UseAutoSaveReturn {
  /** Get auto-saved content for the current item */
  getAutoSavedContent: () => AutoSaveData | null;
  /** Clear auto-saved content for the current item */
  clearAutoSavedContent: () => void;
  /** Check if there's unsaved content */
  hasUnsavedContent: () => boolean;
  /** Check if content was ever saved to cloud */
  hasCloudSave: () => boolean;
  /** Check if content exists (not empty) */
  hasContent: () => boolean;
  /** Check if user is currently typing (content changed but not yet saved) */
  isTyping: () => boolean;
  /** Get all auto-saved items of the same type */
  getAllAutoSavedItems: () => AutoSaveData[];
  /** Clear all auto-saved items of the same type */
  clearAllAutoSavedItems: () => void;
}

/**
 * Hook for auto-saving content to localStorage with debouncing
 */
export function useAutoSave({
  content,
  id,
  type,
  delay = 500,
  enabled = true,
  onAutoSave
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const debouncedContent = useDebounce(content, delay);
  const previousContentRef = useRef<any>(null);
  const lastSavedContentRef = useRef<any>(null);
  const [isCurrentlyTyping, setIsCurrentlyTyping] = useState(false);
  const storageKey = `plot-studio-autosave-${type}`;

  // Track when user is typing (content changed but not yet debounced)
  useEffect(() => {
    if (!enabled || !id) return;

    const currentContentStr = JSON.stringify(content);
    const lastSavedContentStr = JSON.stringify(lastSavedContentRef.current);

    // User is typing if current content differs from last saved content
    const isTypingNow = currentContentStr !== lastSavedContentStr && !isEmptyContent(content, type);
    setIsCurrentlyTyping(isTypingNow);
  }, [content, enabled, id, type]);

  // Save content to localStorage when debounced content changes
  useEffect(() => {
    if (!enabled || !id || !debouncedContent) return;

    // Skip if content hasn't actually changed
    if (JSON.stringify(debouncedContent) === JSON.stringify(previousContentRef.current)) {
      return;
    }

    // Skip if content is empty or just default content
    if (isEmptyContent(debouncedContent, type)) {
      return;
    }

    try {
      const autoSaveData: AutoSaveData = {
        content: debouncedContent,
        timestamp: new Date().toISOString(),
        id,
        type
      };

      // Get existing auto-saved items
      const existingData = getStoredAutoSaveData(storageKey);

      // Update or add the current item
      const updatedData = {
        ...existingData,
        [id]: autoSaveData
      };

      // Save to localStorage
      localStorage.setItem(storageKey, JSON.stringify(updatedData));

      // Update content references
      previousContentRef.current = debouncedContent;
      lastSavedContentRef.current = debouncedContent;

      // User is no longer typing since content was saved
      setIsCurrentlyTyping(false);

      // Call callback if provided
      if (onAutoSave) {
        onAutoSave(autoSaveData);
      }

      console.log(`Auto-saved ${type} content for ID: ${id}`);
    } catch (error) {
      console.error(`Error auto-saving ${type} content:`, error);
    }
  }, [debouncedContent, id, type, enabled, onAutoSave, storageKey]);

  // Get auto-saved content for the current item
  const getAutoSavedContent = useCallback((): AutoSaveData | null => {
    if (!id) return null;

    try {
      const storedData = getStoredAutoSaveData(storageKey);
      return storedData[id] || null;
    } catch (error) {
      console.error(`Error getting auto-saved ${type} content:`, error);
      return null;
    }
  }, [id, type, storageKey]);

  // Clear auto-saved content for the current item
  const clearAutoSavedContent = useCallback(() => {
    if (!id) return;

    try {
      const storedData = getStoredAutoSaveData(storageKey);
      delete storedData[id];
      localStorage.setItem(storageKey, JSON.stringify(storedData));

      // Update lastSavedContentRef to current content to indicate it's now saved
      lastSavedContentRef.current = content;
      setIsCurrentlyTyping(false);

      console.log(`Cleared auto-saved ${type} content for ID: ${id}`);
    } catch (error) {
      console.error(`Error clearing auto-saved ${type} content:`, error);
    }
  }, [id, type, storageKey, content]);

  // Check if there's unsaved content
  const hasUnsavedContent = useCallback((): boolean => {
    const autoSavedData = getAutoSavedContent();
    if (!autoSavedData) return false;

    // Compare current content with auto-saved content
    return JSON.stringify(content) !== JSON.stringify(autoSavedData.content);
  }, [content, getAutoSavedContent]);

  // Check if content was ever saved to cloud
  const hasCloudSave = useCallback((): boolean => {
    if (!id) return false;

    const cloudSaveKey = `plot-studio-cloudsave-${type}`;
    try {
      const cloudSaveData = getStoredAutoSaveData(cloudSaveKey);
      const hasCloudRecord = !!cloudSaveData[id];
      const autoSavedData = getAutoSavedContent();

      // Has cloud save if there's a cloud record AND no current local changes
      return hasCloudRecord && !autoSavedData;
    } catch (error) {
      console.error('Error checking cloud save status:', error);
      return false;
    }
  }, [id, type, getAutoSavedContent]);

  // Check if content exists (not empty)
  const hasContent = useCallback((): boolean => {
    return !isEmptyContent(content, type);
  }, [content, type]);

  // Check if user is currently typing
  const isTyping = useCallback((): boolean => {
    return isCurrentlyTyping;
  }, [isCurrentlyTyping]);

  // Get all auto-saved items of the same type
  const getAllAutoSavedItems = useCallback((): AutoSaveData[] => {
    try {
      const storedData = getStoredAutoSaveData(storageKey);
      return Object.values(storedData);
    } catch (error) {
      console.error(`Error getting all auto-saved ${type} items:`, error);
      return [];
    }
  }, [type, storageKey]);

  // Clear all auto-saved items of the same type
  const clearAllAutoSavedItems = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      console.log(`Cleared all auto-saved ${type} items`);
    } catch (error) {
      console.error(`Error clearing all auto-saved ${type} items:`, error);
    }
  }, [type, storageKey]);

  return {
    getAutoSavedContent,
    clearAutoSavedContent,
    hasUnsavedContent,
    hasCloudSave,
    hasContent,
    isTyping,
    getAllAutoSavedItems,
    clearAllAutoSavedItems
  };
}

/**
 * Helper function to get stored auto-save data
 */
function getStoredAutoSaveData(storageKey: string): Record<string, AutoSaveData> {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error parsing stored auto-save data:', error);
    return {};
  }
}

/**
 * Helper function to check if content is empty or default
 */
function isEmptyContent(content: any, type: 'chapter' | 'note'): boolean {
  if (!content || !Array.isArray(content) || content.length === 0) {
    return true;
  }

  // Check for default empty content
  if (content.length === 1 && content[0]?.type === 'p' &&
    content[0]?.children?.length === 1 &&
    content[0]?.children[0]?.text === '') {
    return true;
  }

  // For notes, check if it's just the default title
  if (type === 'note' && content.length <= 2) {
    const hasOnlyDefaultTitle = content.every((node: any) => {
      if (node.type === 'h1') {
        const text = node.children?.[0]?.text || '';
        return text === 'Untitled Note' || text === '';
      }
      if (node.type === 'p') {
        const text = node.children?.[0]?.text || '';
        return text === '';
      }
      return false;
    });

    if (hasOnlyDefaultTitle) {
      return true;
    }
  }

  return false;
}

/**
 * Utility function to restore auto-saved content on page load
 */
export function restoreAutoSavedContent(id: string, type: 'chapter' | 'note'): AutoSaveData | null {
  const storageKey = `plot-studio-autosave-${type}`;

  try {
    const storedData = getStoredAutoSaveData(storageKey);
    return storedData[id] || null;
  } catch (error) {
    console.error(`Error restoring auto-saved ${type} content:`, error);
    return null;
  }
}

/**
 * Utility function to clear auto-saved content after successful server save
 */
export function clearAutoSavedContentAfterSave(id: string, type: 'chapter' | 'note'): void {
  const storageKey = `plot-studio-autosave-${type}`;
  const cloudSaveKey = `plot-studio-cloudsave-${type}`;

  try {
    // Clear auto-saved content
    const storedData = getStoredAutoSaveData(storageKey);
    delete storedData[id];
    localStorage.setItem(storageKey, JSON.stringify(storedData));

    // Mark as saved to cloud
    const cloudSaveData = getStoredAutoSaveData(cloudSaveKey);
    cloudSaveData[id] = {
      content: null,
      timestamp: new Date().toISOString(),
      id,
      type
    };
    localStorage.setItem(cloudSaveKey, JSON.stringify(cloudSaveData));

    console.log(`Cleared auto-saved ${type} content after successful save for ID: ${id}`);
  } catch (error) {
    console.error(`Error clearing auto-saved ${type} content after save:`, error);
  }
}
